import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d78n0e386jhav3jpfak0';

const categories = [
  'ATS Autofill Issue',
  'Chrome Extension Bug',
  'Application Tracker',
  'AI Resume Tailoring',
  'Account & Billing',
  'Feature Request',
  'Other',
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function SupportContact() {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('submitting');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-16 bg-white dark:bg-[#0A0E1A]">
      <div className="max-w-[860px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left info */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
              Contact Support
            </h2>
            <p className="text-gray-500 dark:text-[#94A3B8] text-sm leading-relaxed mb-8">
              Can&apos;t find what you need? Our team typically responds within 24 hours on business days.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#6366F1]/10 flex-shrink-0">
                  <i className="ri-mail-line text-[#6366F1] text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Email</p>
                  <a href="mailto:hello@jobos.dev" className="text-sm text-[#6366F1] hover:text-[#4F46E5] transition-colors">hello@jobos.dev</a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#6366F1]/10 flex-shrink-0">
                  <i className="ri-twitter-x-line text-[#6366F1] text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Twitter / X</p>
                  <a href="https://x.com/JobosDev" target="_blank" rel="noopener noreferrer" className="text-sm text-[#6366F1] hover:text-[#4F46E5] transition-colors">@JobosDev</a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#6366F1]/10 flex-shrink-0">
                  <i className="ri-time-line text-[#6366F1] text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Response Time</p>
                  <p className="text-sm text-gray-500 dark:text-[#94A3B8]">Within 24 hours (Mon–Fri)</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#6366F1]/10 flex-shrink-0">
                  <i className="ri-book-open-line text-[#6366F1] text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Documentation</p>
                  <a href="https://app.jobos.dev/docs" target="_blank" rel="noopener noreferrer" className="text-sm text-[#6366F1] hover:text-[#4F46E5] transition-colors">app.jobos.dev/docs</a>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-3">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12 bg-gray-50 dark:bg-[#111827] border border-gray-100 dark:border-[#1E2A3B] rounded-xl px-8">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500/10 mb-4">
                  <i className="ri-checkbox-circle-line text-3xl text-emerald-500"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Message Sent!</h3>
                <p className="text-sm text-gray-500 dark:text-[#94A3B8] mb-6 leading-relaxed">
                  Thanks for reaching out. We&apos;ll get back to you within 24 hours on business days.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="px-6 py-2.5 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="support-contact-form"
                onSubmit={handleSubmit}
                className="bg-gray-50 dark:bg-[#111827] border border-gray-100 dark:border-[#1E2A3B] rounded-xl p-6 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Jane Smith"
                      className="w-full h-10 px-3 text-sm bg-white dark:bg-[#0A0E1A] border border-gray-200 dark:border-[#2D3148] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-[#6366F1] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="jane@example.com"
                      className="w-full h-10 px-3 text-sm bg-white dark:bg-[#0A0E1A] border border-gray-200 dark:border-[#2D3148] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-[#6366F1] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    required
                    className="w-full h-10 px-3 text-sm bg-white dark:bg-[#0A0E1A] border border-gray-200 dark:border-[#2D3148] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-[#6366F1] transition-colors cursor-pointer"
                  >
                    <option value="">Select a category…</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="Brief description of your issue"
                    className="w-full h-10 px-3 text-sm bg-white dark:bg-[#0A0E1A] border border-gray-200 dark:border-[#2D3148] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-[#6366F1] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Describe your issue in detail. Include steps to reproduce if it&apos;s a bug."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-3 py-2.5 text-sm bg-white dark:bg-[#0A0E1A] border border-gray-200 dark:border-[#2D3148] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-[#6366F1] transition-colors resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-500' : 'text-gray-400 dark:text-gray-600'}`}>
                    {charCount}/500
                  </p>
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500 flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting' || charCount > 500}
                  className="w-full h-11 bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
